/**
 * Tenant admin hooks — #2366.
 *
 * Thin TanStack Query wrappers around `@/services/tenants`. Every mutation
 * only INVALIDATES its query on success; none writes the submitted body into
 * the cache, so a typed secret never outlives the form that held it.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import {
  tenantIntegrationsAPI,
  tenantBrandingAPI,
  tenantSettingsAPI,
  type IntegrationProvider,
  type OnfleetCredentialsBody,
  type ShopifyCredentialsBody,
  type TenantNameBody,
  type TenantSettingsBody,
} from '@/services/tenants';

export function useTenantIntegrations() {
  return useQuery({
    queryKey: queryKeys.tenantIntegrations.current(),
    queryFn: () => tenantIntegrationsAPI.get(),
  });
}

/** New credentials can see other subscriptions, so the webhook lists refetch too. */
function useInvalidateIntegrations() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.tenantIntegrations.all() });
}

export function useSaveShopifyIntegration() {
  const invalidate = useInvalidateIntegrations();
  return useMutation({
    mutationFn: (body: ShopifyCredentialsBody) => tenantIntegrationsAPI.saveShopify(body),
    // The body holds plaintext secrets; drop it from the MutationCache as
    // soon as nothing observes the mutation (default gcTime would keep it 5 min).
    gcTime: 0,
    onSuccess: invalidate,
  });
}

export function useSaveOnfleetIntegration() {
  const invalidate = useInvalidateIntegrations();
  return useMutation({
    mutationFn: (body: OnfleetCredentialsBody) => tenantIntegrationsAPI.saveOnfleet(body),
    // The body holds plaintext secrets; drop it from the MutationCache as
    // soon as nothing observes the mutation (default gcTime would keep it 5 min).
    gcTime: 0,
    onSuccess: invalidate,
  });
}

export function useRegisterWebhooks(provider: IntegrationProvider) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => tenantIntegrationsAPI.registerWebhooks(provider),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantIntegrations.webhooks(provider) }),
  });
}

/** The provider's subscriptions (#2385); `enabled` is false when nothing is configured. */
export function useIntegrationWebhooks(provider: IntegrationProvider, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.tenantIntegrations.webhooks(provider),
    queryFn: () => tenantIntegrationsAPI.listWebhooks(provider),
    enabled,
  });
}

export function useTenantSettings() {
  return useQuery({
    queryKey: queryKeys.tenantSettings.current(),
    queryFn: () => tenantSettingsAPI.get(),
  });
}

/** A save also refreshes everything under `dispatch` (#2467): flow, board, preview and recent outcomes read these rules. */
export function useSaveTenantSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TenantSettingsBody) => tenantSettingsAPI.save(body),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.tenantSettings.current() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.dispatch.all() }),
      ]),
  });
}

/** Branding writes (#2387): each refetches the branding the sidebar reads. */
function useInvalidateBranding() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.tenantBranding.current() });
}

export function useSaveTenantName() {
  const invalidate = useInvalidateBranding();
  return useMutation({
    mutationFn: (body: TenantNameBody) => tenantBrandingAPI.saveName(body),
    onSuccess: invalidate,
  });
}

export function useUploadTenantLogo() {
  const invalidate = useInvalidateBranding();
  return useMutation({
    mutationFn: (file: File) => tenantBrandingAPI.uploadLogo(file),
    onSuccess: invalidate,
  });
}

export function useDeleteTenantLogo() {
  const invalidate = useInvalidateBranding();
  return useMutation({
    mutationFn: () => tenantBrandingAPI.deleteLogo(),
    onSuccess: invalidate,
  });
}

/** The operator-facing text of a failed request: the server's own message. */
export function requestErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : '';
}
